import { Collapse, IconButton } from "@material-ui/core";
import { Close, Error as ErrorIcon, FiberManualRecord } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { Tooltip } from "@material-ui/core";
import React, { useContext, useEffect, useRef } from "react";
import { connect, ConnectedProps } from "react-redux";
import liveSwitchApp from "../../../../LiveSwitch/LiveSwitchApp";
import ApplicationInsightsContext from "../../../../Shared/Components/ApplicationInsightsContext";
import { DeviceInfo, RecordingOption } from "../../../../Shared/GeneratedTypes";
import ObserverNotification from "../../../../Shared/ObserverNotification";
import { ConnectionQuality, Localizations, ObserverState, OnPremiseSettings } from "../../../../Shared/sharedTypes";
import { ApplicationData } from "../../../types";
import { dismissConnectionWarnings, displayConnectionWarnings } from "../../../../Shared/Redux/Actions/sharedActions";

const mapState = ({ mentor, recordingState }: ApplicationData) => ({ mentor, recordingState });

const mapDispatch = {
    dismissConnectionWarnings
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropTypes = PropsFromRedux & {
    visible: boolean;
    sessionId: string;
    userName: string;
    liveSwitchToken: string;
    deviceId: string;
    menuShown: boolean;
    deviceInfo: DeviceInfo;
    localizations: Localizations;
    activeObservers: ObserverState;
    recordingOption: RecordingOption;
    displayRecordingToMentee: boolean;
    onPremiseSettings: OnPremiseSettings;
    connectionQuality: ConnectionQuality;
    dismissConnectionWarnings: any;
    isRecordingAlertVisible: boolean;
    hideRecordingAlert: () => void;
}

function Video({
    visible,
    sessionId,
    userName,
    liveSwitchToken,
    deviceId,
    menuShown,
    deviceInfo,
    localizations,
    activeObservers,
    recordingOption,
    displayRecordingToMentee,
    onPremiseSettings,
    connectionQuality,
    dismissConnectionWarnings,
    mentor,
    recordingState,
    isRecordingAlertVisible,
    hideRecordingAlert
}: PropTypes) {
    const videoRef = useRef<HTMLDivElement>(null);
    const clientVideo = useRef<HTMLDivElement>(null);
    const appInsights = useContext(ApplicationInsightsContext);

    useEffect(() => {
        startVideo();
        return () => {
            liveSwitchApp.endVideo();
        };
    }, []);

    useEffect(() => {
        if (appInsights != null) {
            appInsights.setAuthenticatedUserContext("CLIENT-" + deviceId);
            appInsights.trackEvent({ name: "CLIENT-VIDEO-SESSION" });
        }
    }, []);

    function startVideo() {
        if (window.fm) {
            if (videoRef.current && clientVideo.current) {
                liveSwitchApp.initializeVideo(
                    videoRef.current,
                    clientVideo.current,
                    sessionId,
                    userName,
                    ["Client"],
                    null,
                    liveSwitchToken,
                    deviceId,
                    deviceInfo?.model || "",
                    deviceInfo?.os?.name || "",
                    appInsights!,
                    false,
                    false,
                    recordingOption !== RecordingOption.Never,
                    onPremiseSettings
                );
                liveSwitchApp.startSession(); 
            }
        } else {
            window.setTimeout(() => startVideo, 100);
        }
    }

    const hide = visible ? "" : "hide";

    const styles = {
        height: (document.body.clientHeight - 140) + 'px'
    };

    function dismissNetworkQualityNotification() {
        dismissConnectionWarnings(connectionQuality?.value ?? 100,
            connectionQuality?.consecutiveDrops ?? 0);
    }

    return (
        <>
            <div className={hide + "policyHolder-video-fillScreen centerAbsolute"} ref={clientVideo} style={styles}></div>

            <div className="videoOverlays">
                <Collapse in={(connectionQuality?.shouldDisplayWarning ?? false)}>
                    <Alert
                        severity="warning"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    dismissNetworkQualityNotification();
                                }}
                            >
                                <Close fontSize="inherit" />
                            </IconButton>
                        }
                        className="video-alert"
                    >
                        {localizations.badNetworkQuality}
                    </Alert>
                </Collapse>

                <Collapse in={(isRecordingAlertVisible ?? false)}>
                    <Alert
                        severity="info"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    hideRecordingAlert();
                                }}
                            >
                                <Close fontSize="inherit" />
                            </IconButton>
                        }
                        className="video-alert"
                    >
                        {recordingState ? localizations.recordingOn : localizations.recordingOff}
                    </Alert>
                </Collapse>
            </div>

            <div className={"mentor-video" + hide + (menuShown ? " shiftMentorVideoDown" : "")}>
                <div ref={videoRef}></div>
                <div className="mentorLabel">
                    <label style={{ float: "left", paddingRight: "1px", paddingLeft: "1px" }}>
                        {mentor?.firstName}{" "} {mentor?.lastName} {mentor?.title ? " - " : ""} {mentor?.title}
                    </label>
                </div>
            </div>
        </>
    );
}

export default connector(Video);
